import { useState, useRef, useEffect, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  DEFAULT_HIGHLIGHT_CLASS,
  DEFAULT_SCROLL_DELAY,
  DEFAULT_SCROLL_BEHAVIOR,
  DEFAULT_SCROLL_BLOCK,
  DEFAULT_ID_FIELD,
  DEFAULT_TABLE_CLASS_NAME,
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_COLUMN_ALIGNMENT,
  EXPANDER_COLUMN_ID,
} from 'constants/common/data-table-constants';

const DATA_TABLE_BLOCK = 'data-table';

/**
 * Generic expandable table component
 * @param {Array} columns - Table column definitions
 * @param {Array} data - Table data
 * @param {Object} scrollToRowId - Scroll configuration object
 * @param {Object} highlightedCellInfo - Cell highlight info { rowId, columnAccessor }
 * @param {Function} onTableReady - Callback when table is ready with table instance
 * @param {Object} config - Additional configuration
 * @param {Function} config.getSubRows - Function to get child rows (default: checks fslis then accounts)
 * @param {string} config.idField - Field name for row ID (default: 'id')
 * @param {string} config.highlightClassName - CSS class for highlighted cells (default: 'highlighted-cell')
 * @param {number} config.scrollDelay - Delay before scrolling in ms (default: 150)
 * @param {string} config.scrollBehavior - Scroll behavior: 'smooth' or 'auto' (default: 'smooth')
 * @param {string} config.scrollBlock - Scroll block position: 'start', 'center', 'end', 'nearest' (default: 'center')
 * @param {Object} config.initialExpanded - Initial expanded state (default: {})
 * @param {string} config.className - Additional CSS class for table
 * @param {Object} config.columnWidths - Column width configuration { columnId: 'width' }. Use 'auto' for flexible width or specific values like '200px', '20%'
 * @param {Object} config.columnAlignments - Column text alignment configuration { columnId: 'left' | 'center' | 'right' }
 */
export const Table = ({
  columns,
  data,
  scrollToRowId,
  highlightedCellInfo,
  onTableReady,
  config = {},
}) => {
  const {
    getSubRows = (row) => row.children,
    idField = DEFAULT_ID_FIELD,
    highlightClassName = DEFAULT_HIGHLIGHT_CLASS,
    scrollDelay = DEFAULT_SCROLL_DELAY,
    scrollBehavior = DEFAULT_SCROLL_BEHAVIOR,
    scrollBlock = DEFAULT_SCROLL_BLOCK,
    initialExpanded = {},
    className = DEFAULT_TABLE_CLASS_NAME,
    columnWidths = {},
    columnAlignments = {},
  } = config;

  const [expanded, setExpanded] = useState(initialExpanded);
  const rowRefs = useRef({});

  const handleExpandedChange = useCallback((updaterOrValue) => {
    setExpanded((prev) => {
      const newValue =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(prev)
          : updaterOrValue;

      // Handle expand all / collapse all
      if (newValue === true || newValue === false) {
        return newValue;
      }

      // For user-initiated toggles, TanStack Table sends the complete new state
      // Don't merge - just use the new state directly
      // This allows users to collapse rows they previously expanded
      return newValue;
    });
  }, []);

  const table = useReactTable({
    data,
    columns,
    state: { expanded },
    onExpandedChange: handleExpandedChange,
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    meta: {
      highlightedCellInfo,
      highlightClassName,
    },
  });

  useEffect(() => {
    if (table && onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  useEffect(() => {
    if (!scrollToRowId?.dataId) return;

    const allRows = table.getRowModel().flatRows;
    const matchingRow = allRows.find(
      (row) => row.original[idField] === scrollToRowId.dataId,
    );

    if (matchingRow) {
      const parts = matchingRow.id.split('.');
      const newExpanded = {};

      for (let i = 1; i < parts.length; i++) {
        newExpanded[parts.slice(0, i).join('.')] = true;
      }

      // Merge with current state to add the parent rows that need to be expanded
      // This preserves any rows the user has manually expanded
      setExpanded((prev) => {
        // If all rows are expanded (prev === true), keep it that way
        if (prev === true) {
          return true;
        }

        // Ensure prev is an object (could be false for collapse all)
        const prevState = typeof prev === 'object' ? prev : {};

        // Only merge in rows that need to be expanded for scrolling
        // Don't override rows that user may have collapsed
        return { ...prevState, ...newExpanded };
      });

      setTimeout(() => {
        rowRefs.current[matchingRow.id]?.scrollIntoView({
          behavior: scrollBehavior,
          block: scrollBlock,
        });
      }, scrollDelay);
    }
  }, [scrollToRowId, table, idField, scrollBehavior, scrollBlock, scrollDelay]);

  const getColumnStyle = useCallback(
    (columnId) => ({
      width: columnWidths[columnId] || DEFAULT_COLUMN_WIDTH,
      textAlign: columnAlignments[columnId] || DEFAULT_COLUMN_ALIGNMENT,
    }),
    [columnWidths, columnAlignments],
  );

  return (
    <table className={`${className} ${DATA_TABLE_BLOCK}`}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                style={getColumnStyle(header.column.id)}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                style={getColumnStyle(cell.column.id)}
                ref={(el) => {
                  if (
                    cell.column.id === EXPANDER_COLUMN_ID ||
                    cell.column.columnDef.accessorKey ===
                      highlightedCellInfo?.columnAccessor
                  ) {
                    rowRefs.current[row.id] = el;
                  }
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
